import { useSectionAccess } from "../auth/useSectionAccess";
import { useEffect, useState, type CSSProperties } from "react";
import { formatDateTime, formatMoney } from "../lib/format";
import { PanelLoading } from "../components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { leadsApi, type ConvertRequest, type ErpGroupOption, type Lead, type LeadStage } from "../api/leads";
import { ConvertLeadDialog } from "../components/leads/ConvertLeadDialog";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import Ic from "../components/icons.jsx";
import { LeadStageBadge } from "../components/LeadStageBadge";
import { CommunicationSection } from "../components/mail/CommunicationSection";
import { countryName, flagEmoji } from "../i18n/countries";
import { LEAD_STAGES, stageColor, tint, canConvert, kindOf } from "./leadPipeline";

/**
 * Lead detail: a pipeline stepper over the fixed 9
 * phases (click = PATCH /stage, free movement), the lead's fields + an
 * editable note, and the conditional convert-to-customer flow. Once converted,
 * the page shows a link to the new company plus a "Napravi ponudu" shortcut
 * that opens the offer editor pre-filled with the new customer.
 */
export default function LeadDetailPage() {
  const { canWrite, canFull } = useSectionAccess("leads");
  const { canWrite: canCreateCompany } = useSectionAccess("companies");
  const { canWrite: canCreateOffer } = useSectionAccess("offers");
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();

  const [lead, setLead] = useState<Lead | null>(null);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState<ErpGroupOption[] | null>(null);
  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [address, setAddress] = useState({ addressLine1: "", postalCode: "", city: "" });
  const [addressBusy, setAddressBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    leadsApi.get(id).then((l) => {
      setLead(l);
      setNote(l.note ?? "");
      setAddress(addressOf(l));
    }).catch((e: ApiError) => toast.error(e.message));
  }, [id, toast]);

  async function changeStage(stage: LeadStage) {
    if (!canWrite || !lead || stage === lead.stage || busy) return;
    // A LOST-kind phase requires an explicit loss reason
    // — the server refuses the move without one.
    let lossReason: string | undefined;
    if (kindOf(stage) === "LOST") {
      const reason = await prompt({
        title: t("leads.lossReason.title", "Lost lead"),
        message: t("leads.lossReason.message", "Why is {{name}} moving to “{{stage}}”? The reason is kept on the lead.", { name: lead.name, stage: t(`leads.stage.${stage}`, stage) }),
        placeholder: t("leads.lossReason.placeholder", "Reason for the loss…"),
        multiline: true,
        required: true,
        danger: true,
        confirmLabel: t("leads.lossReason.confirm", "Move"),
      });
      if (reason == null || !reason.trim()) return;
      lossReason = reason.trim();
    }
    setBusy(true);
    try {
      const updated = await leadsApi.changeStage(lead.id, stage, lossReason);
      setLead(updated);
      toast.success(t("leads.toast.stageChanged", { stage: t(`leads.stage.${stage}`), defaultValue: "Moved to {{stage}}" }));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveNote() {
    if (!canWrite || !lead || noteBusy) return;
    setNoteBusy(true);
    try {
      const updated = await leadsApi.update(lead.id, { note: note.trim() || null });
      setLead(updated);
      toast.success(t("leads.toast.noteSaved", "Note saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setNoteBusy(false);
    }
  }

  async function saveAddress() {
    if (!canWrite || !lead || addressBusy) return;
    setAddressBusy(true);
    try {
      const updated = await leadsApi.update(lead.id, {
        addressLine1: address.addressLine1.trim(),
        postalCode: address.postalCode.trim(),
        city: address.city.trim(),
      });
      setLead(updated);
      setAddress(addressOf(updated));
      toast.success(t("leads.toast.addressSaved", "Address saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setAddressBusy(false);
    }
  }

  /** The dialog needs the customer groups the portal has from the ERP before it opens. */
  async function openConvert() {
    if (!canWrite || !canCreateCompany || busy) return;
    setBusy(true);
    try {
      setConverting(await leadsApi.erpGroups());
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function convert(erp: ConvertRequest) {
    if (!canWrite || !canCreateCompany || !lead || busy) return;
    setBusy(true);
    try {
      const res = await leadsApi.convert(lead.id, erp);
      setLead({ ...lead, convertedCompanyId: res.companyId });
      setConverting(null);
      toast.success(t("leads.toast.convertedToErp", "Customer created and queued for the ERP."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!canFull || !lead || busy) return;
    if (!window.confirm(t("leads.detail.confirmDelete", "Delete this lead?"))) return;
    setBusy(true);
    try {
      await leadsApi.remove(lead.id);
      toast.success(t("leads.toast.deleted", "Lead deleted."));
      navigate("/leads");
    } catch (err) {
      toast.error((err as ApiError).message);
      setBusy(false);
    }
  }

  if (!lead) {
    return <PanelLoading text={t("common.loading")} />;
  }

  const noteDirty = note.trim() !== (lead.note ?? "").trim();
  const saved = addressOf(lead);
  const addressDirty = (["addressLine1", "postalCode", "city"] as const).some((k) => address[k].trim() !== saved[k]);
  const makeOfferHref = `/offers/new?companyId=${encodeURIComponent(lead.convertedCompanyId ?? "")}&customerName=${encodeURIComponent(lead.name)}`;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {converting && (
        <ConvertLeadDialog
          leadName={lead.name}
          leadCountry={lead.country}
          leadAddress={addressOf(lead)}
          groups={converting}
          busy={busy}
          onClose={() => setConverting(null)}
          onConvert={(erp) => void convert(erp)}
        />
      )}
      <div style={{ fontSize: 12.5 }}>
        <Link to="/leads" style={{ color: "var(--ink-3)", textDecoration: "none" }}>
          ← {t("leads.list.title", "Leads")}
        </Link>
      </div>

      <header style={pageHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{lead.name}</h1>
          <LeadStageBadge stage={lead.stage} />
        </div>
        <button type="button" className="btn ghost sm" onClick={() => void remove()} disabled={busy || !canFull}>
          <Ic.x /> {t("leads.detail.delete", "Delete")}
        </button>
      </header>

      {/* Pipeline stepper */}
      <section style={card}>
        <div style={sectionLabel}>{t("leads.detail.pipeline", "Pipeline")}</div>
        <Stepper current={lead.stage} busy={busy || !canWrite} onPick={(s) => void changeStage(s)} />
      </section>

      {/* Fields */}
      <section style={card}>
        <div style={fieldGrid}>
          <FieldRow label={t("leads.field.contactPerson", "Contact person")} value={lead.contactPerson} />
          <FieldRow label={t("leads.field.contactPosition", "Position")} value={lead.contactPosition} />
          <FieldRow label={t("leads.field.email", "Email")} value={lead.email} />
          <FieldRow label={t("leads.field.phone", "Phone")} value={lead.phone} />
          <FieldRow
            label={t("leads.field.country", "Country")}
            value={lead.country ? `${flagEmoji(lead.country)} ${countryName(lead.country, locale)}`.trim() : null}
          />
          <FieldRow
            label={t("leads.field.classification", "Classification")}
            value={lead.classification ? t(`leads.classification.${lead.classification}`, lead.classification) : null}
          />
          <FieldRow label={t("leads.field.source", "Source")} value={lead.source} />
          <FieldRow
            label={t("leads.field.value", "Value")}
            value={lead.value == null ? null : formatMoney(lead.value, lead.currency)}
          />
          <FieldRow label={t("leads.field.owner", "Owner")} value={lead.ownerName} />
          <FieldRow label={t("leads.field.created", "Created")} value={formatDateTime(lead.createdAt)} />
          {lead.lossReason && (
            <FieldRow label={t("leads.field.lossReason", "Loss reason")} value={lead.lossReason} />
          )}
        </div>
      </section>

      {/* Address — carried into the customer's address on conversion */}
      <section style={card}>
        <div style={sectionLabel}>{t("leads.field.address", "Address")}</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <input
            className="input"
            aria-label={t("leads.field.address", "Address")}
            placeholder={t("leads.field.address", "Address")}
            value={address.addressLine1}
            readOnly={!canWrite}
            onChange={(e) => setAddress({ ...address, addressLine1: e.target.value })}
            maxLength={200}
            style={{ flex: 2, minWidth: 220 }}
          />
          <input
            className="input"
            aria-label={t("leads.field.postalCode", "Postal code")}
            placeholder={t("leads.field.postalCode", "Postal code")}
            value={address.postalCode}
            readOnly={!canWrite}
            onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
            maxLength={20}
            style={{ width: 140 }}
          />
          <input
            className="input"
            aria-label={t("leads.field.city", "City")}
            placeholder={t("leads.field.city", "City")}
            value={address.city}
            readOnly={!canWrite}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
            maxLength={120}
            style={{ flex: 1, minWidth: 160 }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" className="btn primary sm" onClick={() => void saveAddress()} disabled={!canWrite || addressBusy || !addressDirty}>
            {addressBusy ? t("leads.detail.savingNote", "Saving…") : t("leads.detail.saveAddress", "Save address")}
          </button>
        </div>
      </section>

      {/* Note */}
      <section style={card}>
        <div style={sectionLabel}>{t("leads.field.note", "Note")}</div>
        <textarea
          className="input"
          value={note}
          readOnly={!canWrite}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={5000}
          placeholder={t("leads.detail.notePlaceholder", "Add a note…")}
          style={{ resize: "vertical" }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" className="btn primary sm" onClick={() => void saveNote()} disabled={!canWrite || noteBusy || !noteDirty}>
            {noteBusy ? t("leads.detail.savingNote", "Saving…") : t("leads.detail.saveNote", "Save note")}
          </button>
        </div>
      </section>

      {/* Correspondence — timeline of mail sent from the portal plus captured replies */}
      <CommunicationSection
        readOnly={!canWrite}
        leadId={lead.id}
        defaultTo={lead.email}
        defaultSubject={t("mail.send.leadSubject", { lead: lead.name, defaultValue: "Bankom — {{lead}}" })}
        offerCompanyId={lead.convertedCompanyId}
      />

      {/* Convert */}
      <section style={card}>
        <div style={sectionLabel}>{t("leads.detail.conversion", "Conversion")}</div>
        {lead.convertedCompanyId ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
              {t("leads.detail.convertedTo", "Converted to a customer company.")}
            </span>
            <Link to={`/admin/companies/${lead.convertedCompanyId}`} className="btn sm" style={{ textDecoration: "none" }}>
              <Ic.factory /> {t("leads.detail.openCompany", "Open company")}
            </Link>
            {canCreateOffer && <Link to={makeOfferHref} className="btn primary sm" style={{ textDecoration: "none" }}>
              <Ic.commercial /> {t("leads.detail.makeOffer", "Make an offer")}
            </Link>}
          </div>
        ) : canConvert(lead.stage) ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ color: "var(--ink-2)", fontSize: 13.5 }}>
              {t("leads.detail.convertHint", "This lead is won — convert it into a customer.")}
            </span>
            <button type="button" className="btn primary sm" onClick={() => void openConvert()} disabled={!canWrite || !canCreateCompany || busy}>
              <Ic.handshake /> {t("leads.detail.convert", "Convert to customer")}
            </button>
          </div>
        ) : (
          <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
            {t("leads.detail.convertLocked", "Only a won (DOBIJEN) lead can be converted into a customer.")}
          </div>
        )}
      </section>
    </div>
  );
}

function Stepper({ current, busy, onPick }: { current: LeadStage; busy: boolean; onPick: (s: LeadStage) => void }) {
  const { t } = useTranslation();
  return (
    <div style={stepperRow}>
      {LEAD_STAGES.map((stage, i) => {
        const color = stageColor(stage);
        const isCurrent = stage === current;
        return (
          <div key={stage} style={stepWrap}>
            {i > 0 && <span aria-hidden style={connector} />}
            <button
              type="button"
              disabled={busy || isCurrent}
              onClick={() => onPick(stage)}
              title={t(`leads.stage.${stage}`, stage)}
              style={stepBtn(isCurrent, color)}
            >
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: "50%", background: color, flex: "none" }} />
              {t(`leads.stage.${stage}`, stage)}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ fontSize: 14, color: value ? "var(--ink)" : "var(--ink-4)" }}>{value || "—"}</span>
    </div>
  );
}

function stepBtn(isCurrent: boolean, color: string): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 12.5,
    fontWeight: 600,
    whiteSpace: "nowrap",
    cursor: isCurrent ? "default" : "pointer",
    color: isCurrent ? "var(--ink)" : "var(--ink-3)",
    background: isCurrent ? tint(color) : "var(--surface)",
    border: `1px solid ${isCurrent ? color : "var(--border)"}`,
  };
}

const pageHead: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" };
const card: CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)", boxShadow: "var(--shadow-1)", padding: 18 };
const sectionLabel: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 };
const fieldGrid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 };
const fieldLabel: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
const stepperRow: CSSProperties = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0, rowGap: 8 };
const stepWrap: CSSProperties = { display: "flex", alignItems: "center" };
const connector: CSSProperties = { width: 16, height: 2, marginRight: 6, background: "var(--border)" };

/** The address fields of a lead, empty where it has none. */
function addressOf(l: Lead) {
  return { addressLine1: l.addressLine1 ?? "", postalCode: l.postalCode ?? "", city: l.city ?? "" };
}
